import type { AnyAsyncFunction, AnyFunction } from "@nova/constants"

export const isEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true
    if (a === null || b === null) return false
    if (typeof a !== typeof b) return false
    if (typeof a !== "object") return false
    if (Array.isArray(a) !== Array.isArray(b)) return false
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b as object)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) =>
        isEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
    )
}

export const clamp = (n: number, lower: number, upper: number) =>
    Math.min(Math.max(n, lower), upper)

export const inRange = (n: number, start: number, end: number) => {
    return n >= start && n < end
}

export const sum = (arr: number[]) => arr.reduce((acc, cur) => acc + cur, 0)

export const capitalize = (str: string) =>
    str.length === 0 ? "" : str[0].toUpperCase() + str.slice(1).toLowerCase()

export const toPath = (path: string): string[] =>
    path
        .replace(/\[(\d+)\]/g, ".$1")
        .split(".")
        .filter(Boolean)

export const findKey = <T extends object>(
    obj: T,
    predicate: (value: T[keyof T], key: string) => boolean
) => {
    for (const [key, value] of Object.entries(obj)) {
        if (predicate(value as T[keyof T], key)) return key
    }
    return undefined
}

/** @deprecated - dont use this */
export function get<TObject extends object, TKey extends keyof TObject>(
    obj: TObject,
    path: TKey | [TKey]
): TObject[TKey]
export function get<TObject extends object, TKey extends keyof TObject, TDefault>(
    obj: TObject | null | undefined,
    path: TKey | [TKey],
    defaultValue: TDefault
): Exclude<TObject[TKey], undefined> | TDefault
export function get<TObject extends object, TKey extends keyof TObject>(
    obj: TObject | null | undefined,
    path: TKey | [TKey]
): TObject[TKey] | undefined
export function get<TDefault>(
    obj: unknown,
    path: string | (string | number)[],
    defaultValue: TDefault
): TDefault
export function get(obj: unknown, path: string | (string | number)[]): unknown
export function get(
    obj: unknown,
    path: string | (string | number)[],
    defaultValue?: unknown
): unknown {
    const keys = Array.isArray(path) ? path : typeof path === "number" ? [path] : toPath(path)
    let result: unknown = obj
    for (const key of keys) {
        if (result == null) return defaultValue
        result = (result as Record<string, unknown>)[key]
    }
    return result === undefined ? defaultValue : result
}

/** @deprecated - dont use this */
export const set = <T extends object>(
    obj: T,
    path: string | (string | number)[],
    value: unknown
): T => {
    const keys = Array.isArray(path) ? [...path] : toPath(path)
    let current = obj as Record<string | number, unknown>
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i]
        const nextKey = keys[i + 1]
        if (current[key] == null || typeof current[key] !== "object") {
            current[key] = typeof nextKey === "number" || /^\d+$/.test(nextKey) ? [] : {}
        }
        current = current[key] as Record<string | number, unknown>
    }
    current[keys[keys.length - 1]] = value
    return obj
}

/** @deprecated - dont use this */
export const has = (obj: unknown, path: string | string[]): boolean => {
    if (obj == null) return false
    const keys = Array.isArray(path) ? path : toPath(path)
    let current: unknown = obj
    for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(current, key)) return false
        current = (current as Record<string, unknown>)[key]
    }
    return true
}

export const keys = Object.keys

export const pick = <T extends object, K extends keyof T>(obj: T, paths: K[]): Pick<T, K> =>
    paths.reduce(
        (acc, key) => {
            if (key in obj) acc[key] = obj[key]
            return acc
        },
        {} as Pick<T, K>
    )

export const omit = <T extends object, K extends keyof T>(
    obj: T,
    paths: (K | string)[]
): Omit<T, K> => {
    const result = { ...obj }
    for (const key of paths) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        if (key in result) delete result[key as keyof T]
    }
    return result
}

export const last = <T>(arr: T[]): T | undefined => arr[arr.length - 1]

export const toggle = <T>(values: T[], value: T) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value]

export const cloneDeep = <T>(value: T) => structuredClone(value)

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = (): void => {}

export const now = Date.now

export const once = <T extends AnyFunction>(fn: T): T => {
    let called = false
    let result: unknown
    return ((...args: Parameters<T>) => {
        if (!called) {
            called = true
            result = fn(...args)
        }
        return result
    }) as T
}

export const memoize = <T extends AnyFunction>(
    fn: T,
    resolver?: (...args: Parameters<T>) => unknown
): T => {
    const cache = new Map<unknown, unknown>()
    const memoized = (...args: Parameters<T>) => {
        const key = resolver ? resolver(...args) : (args[0] as string) || "default"
        if (cache.has(key)) return cache.get(key)
        const result = fn(...args) as ReturnType<T>
        cache.set(key, result)
        return result
    }
    return memoized as T
}

type Cancelable = { cancel: () => void }

export const debounce = <T extends AnyFunction>(
    fn: T,
    wait: number
): ((...args: Parameters<T>) => void) & Cancelable => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const debounced = (...args: Parameters<T>) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
            timer = null
            fn(...args)
        }, wait)
    }
    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
    }
    return debounced
}

export const throttle = <T extends AnyFunction>(
    fn: T,
    limit: number
): ((...args: Parameters<T>) => void) & Cancelable => {
    let lastCall = 0
    let timer: ReturnType<typeof setTimeout> | null = null
    const throttled = (...args: Parameters<T>) => {
        const now = Date.now()
        const remaining = limit - (now - lastCall)
        if (remaining <= 0) {
            if (timer) {
                clearTimeout(timer)
                timer = null
            }
            lastCall = Date.now()
            fn(...args)
        } else if (!timer) {
            timer = setTimeout(() => {
                lastCall = Date.now()
                timer = null
                fn(...args)
            }, remaining)
        }
    }
    throttled.cancel = () => {
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
    }
    return throttled
}

/**
 * Promisified throttle. Calls that arrive within the throttle window are
 * queued (up to maxQueueSize). When the window expires, fn is called once
 * with the most recent args and every queued promise resolves with that result.
 */
export const throttleAsync = <T extends AnyAsyncFunction>(
    fn: T,
    timeout: number,
    maxQueueSize = 10
): ((...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>>) => {
    type R = Awaited<ReturnType<T>>
    type Entry = { args: Parameters<T>; resolve: (v: R) => void; reject: (e: unknown) => void }

    let timer: ReturnType<typeof setTimeout> | null = null
    const queue: Entry[] = []

    const shift = () => {
        timer = null
        if (queue.length === 0) return
        const { args, resolve, reject } = queue.shift()!
        ;(fn(...args) as Promise<R>).then(resolve).catch(reject)
        timer = setTimeout(shift, timeout)
    }

    const throttled = (...args: Parameters<T>) =>
        new Promise<R>((resolve, reject) => {
            queue.push({ args, resolve, reject })
            if (queue.length > maxQueueSize) queue.shift()?.reject(new Error("Pushed out of queue"))
            if (!timer) shift()
        })

    return throttled
}

/**
 * Like throttleAsync but without a queue. The first call in a quiet period fires fn immediately.
 * Calls while a promise is pending will get that in-flight promise.
 * Calls without a pending promise within the throttle window are collapsed: fn fires once at window
 * expiry with the mose recent args, and every waiting caller resolves with that shared result.
 */
export const throttleAsyncTrailing = <T extends AnyAsyncFunction>(
    fn: T,
    timeout: number
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
    type R = ReturnType<T>

    let pending:
        | { promise: Promise<R>; timer: Promise<void> }
        | { promise: undefined; timer: Promise<void> }
        | { promise: undefined; timer: undefined } = { promise: undefined, timer: undefined }

    const createPending = (fn: Promise<R>) => {
        if (pending.timer) return pending as { promise: Promise<R>; timer: Promise<void> }
        const nextPending = {
            timer: new Promise<void>((res) => {
                setTimeout(() => {
                    pending = { promise: undefined, timer: undefined }
                    res()
                }, timeout)
            }),
            promise: fn.then((res) => {
                pending.promise = undefined
                return res
            }),
        }
        pending = nextPending
        // without the cast the pending shape infers as Promise<any> and trips no-unsafe-return
        // oxlint-disable-next-line typescript/no-unnecessary-type-assertion
        return nextPending as { promise: Promise<R>; timer: Promise<void> }
    }

    return async (...args) => {
        if (!pending.timer) {
            return createPending(fn(...args) as Promise<R>).promise
        } else if (!pending.promise) {
            await pending.timer
            return createPending(fn(...args) as Promise<R>).promise
        } else return pending.promise
    }
}

export const collect = <T>(
    fn: (items: T[]) => void,
    time: number,
    leading = true
): ((item: T, onCompletion?: () => void) => void) => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const items = [] as T[]
    const callbacks = [] as (() => void)[]
    let firstRun = leading
    return (item: T, onCompletion?: () => void) => {
        if (firstRun) {
            firstRun = false
            fn(items)
            onCompletion?.()
        }
        items.push(item)
        if (onCompletion) callbacks.push(onCompletion)
        if (timeout) return
        else {
            timeout = setTimeout(() => {
                try {
                    fn(items)
                } finally {
                    items.length = 0
                    timeout = undefined
                    // Execute all callbacks after dispatch.
                    callbacks.forEach((callback) => {
                        callback()
                    })
                    callbacks.length = 0
                }
            }, time)
        }
    }
}

let _uniqueIdCounter = 0
export const uniqueId = (prefix = ""): string => `${prefix}${++_uniqueIdCounter}`

export const arrToMap = <T extends object>(arr: T[], key: keyof T) =>
    arr.reduce<Record<string, T | undefined>>((acc, cur) => {
        acc[cur[key] as string] = cur
        return acc
    }, {})

/** value is not null and not undefined. Allows empty strings. */
export const isDefined = <T>(arg: T | null | undefined): arg is T =>
    arg !== undefined && arg !== null

export const didNotFail = <T>(
    arg: T | { failed: true; err: unknown } | null | undefined
): arg is T => arg !== undefined && arg !== null && (arg as { failed?: unknown }).failed !== true

export const omitUndefined = <T extends Record<string, unknown>>(
    obj: T
): { [K in keyof T]: Exclude<T[K], undefined> } =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as {
        [K in keyof T]: Exclude<T[K], undefined>
    }

type Task<T = unknown> = () => Promise<T>
export const createSingleTaskRunner = () => {
    let nextTask = null as Task | null
    let isRunning = false

    const execute = async (task: Task) => {
        isRunning = true
        try {
            await task()
        } catch (error) {
            console.error("Task failed:", error)
        } finally {
            if (nextTask) {
                const taskToRun = nextTask
                nextTask = null
                void execute(taskToRun)
            } else {
                isRunning = false
            }
        }
    }

    return {
        run: async (task: Task) => {
            if (!isRunning) {
                isRunning = true
                await execute(task)
            } else {
                nextTask = task
            }
        },
    }
}

export function byKey<T extends { [K in D]: unknown }, D extends keyof T, V extends T[D]>(
    key: D,
    value: V
) {
    return (item: T): item is Extract<T, { [K in D]: V }> => item[key] === value
}

export const weakMemoize = <T extends object, R>(fn: (arg: T) => R): ((arg: T) => R) => {
    const cache = new WeakMap<T, R>()
    return (arg: T) => {
        if (cache.has(arg)) return cache.get(arg)!
        const result = fn(arg)
        cache.set(arg, result)
        return result
    }
}

export function lazy<T extends NonNullable<unknown>>(fn: () => T): () => T {
    let value: T | undefined
    return () => (value ??= fn())
}

export function lazyWithExpiration<T extends NonNullable<unknown>>(
    fn: () => T,
    expMs: number
): () => T {
    let value: T | undefined
    let expiresAt = 0
    return () => {
        if (value === undefined || Date.now() >= expiresAt) {
            value = fn()
            expiresAt = Date.now() + expMs
        }
        return value
    }
}

export const dedupe = <T>(values: T[], normalize: (value: T) => unknown = (v) => v) => {
    const seen = new Set<unknown>()
    return values.filter((value) => {
        const key = normalize(value)
        if (!key) return true
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}
